import { createInitialFamilyTree } from './initial'
import { layoutFamilyTree } from './layout'
import getPersonData from './queries/getPersonData'

export const familyTreeDefaultConfig = {
  familyTree: {
    verticalSpacing: 200,
    horizontalSpacing: 250,
    slotCount: 50,
    defaultNodeType: 'familyTreeNode',
    autoSaveOnAdd: true,
    canEditContent: true,
    defaultSlotData: [{
      levelTypeId: 200,
      data: {}
    }],
    displayOptions: {
      imageAccessor: 'entityProps.data.profilePic',
      headerAccessor: 'entityProps.data.label',
      descriptionAccessor: 'entityProps.data.birthDate'
    }
  },
  canvasSettings: {
    pathType: 'smoothstep',
    hideHandlers: false,
    nodesEditable: true,
    showToolbar: true,
    canSeeDetails: true
  },
  nodeLinking: {
    levels: ['person'],
    enabled: true
  }
}

let idCounter = 0
const nextId = () => `familyTree_${Date.now()}_${idCounter++}`

export const familyTreePlugin = {
  key: 'familyTree',
  defaultConfig: familyTreeDefaultConfig,
  createInitial: createInitialFamilyTree,
  layout: layoutFamilyTree,
  buildInitialLayout: ({ config, clientId }) => {
    const initial = createInitialFamilyTree(config, clientId)
    return { nodes: initial.nodes, edges: initial.edges || [] }
  },
  getLinkedEntitiesFromNodes: (nodes) => (nodes || []).map((node) => node?.data?.entityProps).filter(Boolean),
  fetchSlotData: async ({ slotData, levelTypeId, levelId }) => {
    if (!slotData?.levelTypeId) return null

    const queryMap = {
      200: getPersonData
    }

    const query = queryMap[slotData?.levelTypeId]
    if (!query) {
      console.warn(`No query mapping for levelTypeId: ${slotData?.levelTypeId}`)
      return null
    }

    const clientId = levelTypeId === 201 ? levelId : null
    if (!clientId) return null

    return {
      levelTypeId: slotData?.levelTypeId,
      data: await query(clientId)
    }
  },
  createNodesFromSlotData: (peopleData, config) => {
    const nodes = []
    const edges = []

    const displayOptions = config?.familyTree?.displayOptions || {}
    const imageAccessor = displayOptions.image || 'entityProps.data.profilePic'
    const headerAccessor = displayOptions.header || 'entityProps.data.label'
    const descriptionAccessor = displayOptions.description || 'entityProps.data.birthDate'

    // Find the minimum depth to use as relative baseline
    const minDepth = Math.min(...peopleData.map(p => p.personType?.familyTreeDepth ?? 0))

    peopleData.forEach((person) => {
      const nodeId = nextId()

      const absoluteDepth = person.personType?.familyTreeDepth ?? 99
      const relativeDepth = absoluteDepth - minDepth

      const nodeSize = relativeDepth === 0 ? 'lg'
        : relativeDepth === 1 ? 'md'
          : 'sm'

      nodes.push({
        id: nodeId,
        type: 'familyTreeNode',
        position: { x: 0, y: 0 },
        data: {
          nodeProps: { size: nodeSize, handles: [], targets: [] },
          entityProps: {
            type: 'person',
            levelId: person.levelId,
            levelTypeId: person.levelTypeId,
            data: person,
            familyTreeDepth: person.familyTreeDepth
          },
          displayOptions: {
            image: imageAccessor,
            header: headerAccessor,
            description: descriptionAccessor
          }
        }
      })
    })

    const nodesByDepth = {}
    nodes.forEach(node => {
      const depth = node.data.entityProps.familyTreeDepth ?? 0
      if (!nodesByDepth[depth]) {
        nodesByDepth[depth] = []
      }
      nodesByDepth[depth].push(node)
    })

    const depths = Object.keys(nodesByDepth).map(Number).sort((a, b) => a - b)

    for (let i = 0; i < depths.length - 1; i++) {
      const currentDepth = depths[i]
      const nextDepth = depths[i + 1]
      const currentNodes = nodesByDepth[currentDepth]
      const nextNodes = nodesByDepth[nextDepth]

      currentNodes.forEach(parentNode => {
        nextNodes.forEach(childNode => {
          edges.push({
            id: `${parentNode.id}->${childNode.id}`,
            source: parentNode.id,
            target: childNode.id,
            sourceHandle: 'handle_bottom',
            targetHandle: 'target_top',
            type: 'smoothstep'
          })
        })
      })
    }

    const rootNode = nodes.find(n => n.data.entityProps.familyTreeDepth === 0) || nodes[0]
    const centerId = rootNode?.id

    const laidOut = layoutFamilyTree({
      nodes,
      edges,
      centerId,
      config
    })

    return {
      nodes: laidOut.nodes,
      edges: laidOut.edges
    }
  },
  interactions: {
    nodeCreationEnabled: true,
    nodeLinkingEnabled: true
  }
}
